import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { blue, red } from '@mui/material/colors';
import { addMonths, format, isBefore } from 'date-fns';
import Formsy from 'formsy-react';
import { Map } from 'immutable';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import HorizontalDateBox from '@sophocles/components/HorizontalDateBox';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import {
  fetchInternationalMeterReadingForSiteWithDateRange,
  getInternationalMeterReadingsForSelectedSite,
} from '@sophocles/entities/InternationalMeterReading';
import useDateRangeSearchParams from '@sophocles/hooks/useDateRangeSearchParams';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import InternationalMeterReadingsGrid from './components/InternationalMeterReadingsGrid';

export function InternationalMeterReadingsViewPage(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { selectedStructure, loading: dashboardLoading } = useSiteDashboardLayoutContext();
  const selectedStructureId = params.site_id;

  const meterReadings = useSelector(getInternationalMeterReadingsForSelectedSite);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const defaultStart = useMemo(() => addMonths(new Date(), -2), []);
  const defaultEnd = useMemo(() => new Date(), []);
  const { startDate, endDate, onDateRangeChange } = useDateRangeSearchParams({
    defaultStart,
    defaultEnd,
  });

  const fetchData = useCallback(
    (start, end) => {
      if (selectedStructureId == null) return;
      setLoading(true);
      dispatch(
        fetchInternationalMeterReadingForSiteWithDateRange(
          selectedStructureId,
          format(start, 'yyyy-MM-dd'),
          format(end, 'yyyy-MM-dd'),
        ),
      ).then(() => setLoading(false));
    },
    [dispatch, selectedStructureId],
  );

  useEffect(() => {
    fetchData(startDate, endDate);
    // run on mount + when the underlying inputs change
  }, [fetchData, startDate, endDate]);

  const startDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(date, endDate);
    },
    [onDateRangeChange, endDate],
  );

  const endDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(startDate, date);
    },
    [onDateRangeChange, startDate],
  );

  const menuClick = useCallback(
    (e, url) => {
      e.preventDefault();
      navigate(url);
    },
    [navigate],
  );

  if (dashboardLoading) {
    return <LoadingScreen />;
  }

  const unit = selectedStructure.get('relay_controller', Map());
  const isStaff = user.get('is_staff', false);
  const measuresPower = unit.get('measures_power_consumption_in_watts', false);
  const measuresComplexPower = unit.getIn(['model', 'measures_complex_ac_power'], false);
  const powerOrCurrent = measuresPower ? t('powerData.power', 'Power') : t('powerData.current', 'Current');

  const startDateTime = startDate.startOf?.('day') ?? startDate;
  const endDateTime = endDate.endOf?.('day') ?? endDate;
  const dataList = meterReadings.filter(data => {
    const m = data.get('timestamp');
    return m.isAfter(startDateTime) && m.isBefore(endDateTime);
  });

  const dateStartParam = searchParams.get('date_start');
  const dateEndParam = searchParams.get('date_end');
  const dateParams = dateStartParam && dateEndParam ? `?date_end=${dateEndParam}&date_start=${dateStartParam}` : '';

  const menuOpen = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        paddingBottom: 'var(--page-py)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 40,
          mb: 1,
        }}
      >
        <Formsy className="form-inline">
          <HorizontalDateBox
            title={t('sites.startDate', 'Start Date')}
            name="start_date"
            value={startDate}
            labelClassName="no-bottom-margin pad-right"
            inputDivClassName="inline"
            inputClassName="form-control form-control-sm"
            outerClassName="form-group"
            onChange={startDateFilter}
          />
          &nbsp; &nbsp;
          <HorizontalDateBox
            title={t('sites.endDate', 'End Date')}
            name="end_date"
            value={endDate}
            labelClassName="no-bottom-margin pad-right"
            inputDivClassName="inline"
            inputClassName="form-control form-control-sm"
            outerClassName="form-group"
            rightAlign
            onChange={endDateFilter}
            isValidDate={current => isBefore(current, new Date())}
          />
        </Formsy>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!measuresComplexPower ? (
            <span style={{ color: red[500], paddingRight: 5 }}>{t('powerData.noSupportForFeature')}</span>
          ) : null}
          {measuresComplexPower || isStaff ? (
            <Fragment>
              <IconButton onClick={event => setAnchorEl(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClick={() => setAnchorEl(null)}
                onClose={() => setAnchorEl(null)}
              >
                <a
                  href="#"
                  onClick={e =>
                    menuClick(
                      e,
                      `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power${dateParams}`,
                    )
                  }
                >
                  <MenuItem>{`${powerOrCurrent} ${t('powerData.readings')}`}</MenuItem>
                </a>
                <a
                  href="#"
                  onClick={e =>
                    menuClick(
                      e,
                      `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/consumption${dateParams}`,
                    )
                  }
                >
                  <MenuItem style={measuresComplexPower ? null : { color: red[500] }}>
                    {t('pages.powerConsumption')}
                  </MenuItem>
                </a>
                {isStaff ? (
                  <a
                    href="#"
                    onClick={e =>
                      menuClick(
                        e,
                        `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/meter${dateParams}`,
                      )
                    }
                  >
                    <MenuItem style={{ color: blue[500] }}>{t('pages.internationalMeterReadings')}</MenuItem>
                  </a>
                ) : null}
              </Menu>
            </Fragment>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <InternationalMeterReadingsGrid list={dataList} />
      </Box>
    </Box>
  );
}

export default InternationalMeterReadingsViewPage;
